import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ScrollBox } from 'components/atoms/ScrollBox';
import styled from 'styled-components';
import { Box } from 'rebass/styled-components';
import { Accordion } from '../../../components/organisms/contactsFilters/components/accordion';
import { createMovementsFilterGroups } from './movementsFilterGroups';
import { defaultFilters } from '../defaultFilters';

const AccordionContainer = styled.div`
  width: 100%;

  && label {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.main};
  }

  && > div > div:last-child {
    border-bottom: none;
  }
`;

const countFilters = (filtersToCount) => {
  let count = 0;
  Object.keys(filtersToCount).forEach((key) => {
    const value = filtersToCount[key];

    if (value === null || value === undefined) return;

    if (Array.isArray(value) && value.length > 0) {
      count += 1;
      return;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      if (value.value === '' || value.value === null) return;
      if (Object.keys(value).length > 0) count += 1;
      return;
    }

    if (value === '' || value === defaultFilters[key]) return;

    count += 1;
  });
  return count;
};

export const MovementsFilters = memo(({ filters, setFilters }) => {
  const setMultipleFilters = useCallback(
    (group) => {
      setFilters(group);
    },
    [setFilters],
  );

  const groups = useMemo(
    () =>
      createMovementsFilterGroups({
        filters,
        setFilters: setMultipleFilters,
      }).reduce((acc, { id: groupId, title, show, filters: groupFilters }) => {
        if (show) {
          const filtersToCount = {};
          const currentGroup = {
            id: groupId,
            title,
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {groupFilters.reduce(
                  (acc2, { id: filterId, Component, props, show: showFilter, additionalCountKeys }) => {
                    if (showFilter && Component) {
                      acc2.push(<Component key={filterId} {...props} />);

                      const filterValue = filters[filterId];

                      if (filterValue !== null && filterValue !== undefined) {
                        if (Array.isArray(filterValue) && filterValue.length > 0) {
                          filtersToCount[filterId] = filterValue;
                        } else if (typeof filterValue === 'object' && !Array.isArray(filterValue)) {
                          if (filterValue.value !== '' && filterValue.value !== null) {
                            if (Object.keys(filterValue).length > 0) {
                              filtersToCount[filterId] = filterValue;
                            }
                          }
                        } else if (
                          !Array.isArray(filterValue) &&
                          filterValue !== '' &&
                          filterValue !== defaultFilters[filterId]
                        ) {
                          filtersToCount[filterId] = filterValue;
                        }
                      }

                      if (additionalCountKeys) {
                        additionalCountKeys.forEach((key) => {
                          const additionalValue = filters[key];
                          if (additionalValue !== null && additionalValue !== undefined) {
                            if (Array.isArray(additionalValue) && additionalValue.length > 0) {
                              filtersToCount[key] = additionalValue;
                            } else if (typeof additionalValue === 'object' && !Array.isArray(additionalValue)) {
                              if (additionalValue.value !== '' && additionalValue.value !== null) {
                                if (Object.keys(additionalValue).length > 0) {
                                  filtersToCount[key] = additionalValue;
                                }
                              }
                            } else if (
                              !Array.isArray(additionalValue) &&
                              additionalValue !== '' &&
                              additionalValue !== defaultFilters[key]
                            ) {
                              filtersToCount[key] = additionalValue;
                            }
                          }
                        });
                      }
                    }

                    return acc2;
                  },
                  [],
                )}
              </div>
            ),
          };

          const count = countFilters(filtersToCount);
          if (count > 0) {
            currentGroup.badge = `${count}`;
          }

          acc.push(currentGroup);
        }

        return acc;
      }, []),
    [filters, setMultipleFilters],
  );

  return (
    <ScrollBox
      data-testid="movements-filters"
      sx={{
        top: 0,
        height: '100%',
        padding: '0 16px',
      }}
    >
      <Box
        sx={{
          gap: '12px',
          position: 'relative',
          paddingBottom: '16px',
        }}
      >
        <AccordionContainer>
          <Accordion items={groups} />
        </AccordionContainer>
      </Box>
    </ScrollBox>
  );
});

MovementsFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  filtersCount: PropTypes.number,
};

MovementsFilters.defaultProps = {
  filtersCount: 0,
};
